import * as Phaser from 'phaser';
import { emitContinue, emitHintGranted, emitRestart, emitSetLevel, emitTimePause, emitTimeResume,
  gameEventNames, onGameEvent } from './game/events';
import { difficultyLabelForLevel } from './game/difficultyLabel';
import { intensityForLevel } from './generation/curve';
import { clampLevelNumber, ensureLevelLoaded, getEntry, maximumLevelNumber,
  minimumLevelNumber } from './game/levels';
import { loadProgress, recordClear, setCurrentLevel } from './game/progress';
import { buildResultViewModel } from './game/result';
import { PuzzleScene } from './game/scenes/PuzzleScene';
import { WebPlatformAdapter } from './platform/PlatformAdapter';
import { AdsService } from './platform/ads/AdsService';
import { createAdFlow } from './shell/adFlow';
import { createConsentDialog } from './shell/consentDialog';
import { createContinueDialog } from './shell/continueDialog';
import { createDevPanel, type DevPanelApi } from './shell/devPanel';
import { createHeader } from './shell/header';
import { createLevelFlow } from './shell/levelFlow';
import { createModals } from './shell/modals';
import { createResultView } from './shell/resultView';
import { createStatsFlow } from './shell/statsFlow';
import { createStatsView } from './shell/statsView';
import { createToolbar } from './shell/toolbar';
import { selectLiffId } from './sync/liffId';
import {
  createConfiguredAuthClient,
  createConfiguredProgressSync,
  normalizeApiBase,
} from './sync/service';
import { createConfiguredStats } from './sync/stats';
import './styles.css';

// 初期化順: platform → ads → level load → shell factories → header 初期描画
// → gameEvents wiring → DEV panel → Phaser。
const platform = new WebPlatformAdapter();
await platform.initialize();
const adProvider = platform.createAdProvider();
const adsService = new AdsService(adProvider);
const rewardProvider = adProvider.kind === 'mock' || adProvider.kind === 'lineyahoo'
  ? adProvider.kind
  : null;
const syncOptions = {
  apiBase: import.meta.env.VITE_API_BASE,
  liffId: selectLiffId(window.location.pathname, {
    VITE_LIFF_ID: import.meta.env.VITE_LIFF_ID,
    VITE_LIFF_ID_REVIEW: import.meta.env.VITE_LIFF_ID_REVIEW,
  }),
  devSecret: import.meta.env.DEV ? import.meta.env.VITE_AUTH_DEV_SECRET : undefined,
  development: import.meta.env.DEV,
};
// 進捗同期と分析表示で 1 つの認証クライアントを共有する。別々に作ると
// LIFF の初期化と ID トークン交換が二重に走る。
const apiBase = normalizeApiBase(syncOptions.apiBase);
const authClient = apiBase === null
  ? undefined
  : createConfiguredAuthClient(apiBase, syncOptions);
const progressSync = createConfiguredProgressSync({ ...syncOptions, authClient });
// 分析表示も同じ方針。取得できたときだけ後から差し込む。
const statsFlow = createStatsFlow({
  stats: createConfiguredStats({ ...syncOptions, authClient }),
  view: createStatsView(),
});
// 同期の失敗や遅延でゲーム起動を止めない。initialize 内の順序は
// outbox flush → progress GET → max merge のまま、バックグラウンドで進める。
// 表示のためだけの分析取得はその後ろに置く（起動直後のセッション発行と
// 進捗マージを優先し、認証トークンも初期化で温まったものを使い回す）。
void progressSync.initialize().then(() => statsFlow.start());
let game: Phaser.Game | null = null;
let devPanel: DevPanelApi | null = null;

const levelFlow = createLevelFlow({
  isDev: import.meta.env.DEV,
  getLocationHref: () => window.location.href,
  loadCurrentLevel: () => loadProgress().currentLevel,
  saveCurrentLevel: (levelNumber) => setCurrentLevel(levelNumber).currentLevel,
  clampLevelNumber,
  maximumLevelNumber,
  ensureLevelLoaded,
  adFlow: { resetTransientState: () => adFlow.resetTransientState() },
  emitSetLevel,
  sync: syncToolbarButtons,
  restoreResultActions: (isFinalLevel) => resultView.restoreActions(isFinalLevel),
  setActiveModal: (modal) => modals.setActive(modal),
  dispatchRestart: emitRestart,
  attemptBegan: (levelNumber) => { progressSync.attemptBegan(levelNumber); },
});
await levelFlow.initialize();
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

const continueDialogView = createContinueDialog({
  reducedMotionQuery,
  onContinue: () => adFlow.requestContinue(),
  onRestart: () => adFlow.requestRestart(),
});
const consentDialogView = createConsentDialog({
  onAccept: () => void adFlow.acceptConsent(),
  onCancel: () => adFlow.cancelConsent(),
});

function isModalOrAdFlowActive(includeAdFlow: boolean): boolean {
  return modals.active !== 'none' || (includeAdFlow && adFlow.active);
}

function syncToolbarButtons(): void {
  const blocked = levelFlow.resultPending || isModalOrAdFlowActive(false);
  toolbar.sync(blocked);
  devPanel?.sync(isModalOrAdFlowActive(true));
}

const modals = createModals({
  getGame: () => game,
  onResultShown: () => resultView.onShown(),
  onResultHidden: () => resultView.onHidden(),
  emitTimePause,
  emitTimeResume,
  sync: syncToolbarButtons,
  focusContinueHeading: continueDialogView.focusHeading,
  isContinueHeadingFocused: continueDialogView.isHeadingFocused,
  focusConsentHeading: consentDialogView.focusHeading,
  isConsentHeadingFocused: consentDialogView.isHeadingFocused,
});

const adFlow = createAdFlow({
  adsService,
  levelFlow,
  modals,
  continueDialog: continueDialogView,
  consentDialog: consentDialogView,
  emitContinue,
  emitHintGranted,
  recordReward: (rewardType) => {
    if (rewardProvider !== null) progressSync.rewardGranted(rewardProvider, rewardType);
  },
  setHeaderWarning: (message) => header.setMessage(message, 'warning'),
  disableResultActions: () => resultView.disableActions(),
  sync: syncToolbarButtons,
});

const toolbar = createToolbar({ onRestart: levelFlow.restartGame, onHint: adFlow.requestHint });
const resultView = createResultView({
  reducedMotionQuery,
  intensityForLevel,
  getFallDistance: modals.getFallDistance,
  onNext: () => void adFlow.requestNextLevel(),
  // 暫定仕様: 結果画面の「もう一度」と通常のツールバー「やり直す」には広告を付けない。
  onPlayAgain: () => {
    resultView.disableActions();
    void levelFlow.replayCurrentLevel();
  },
});

const header = createHeader({
  initialLevelNumber: levelFlow.currentLevel,
  clamp: Phaser.Math.Clamp,
  difficultyLabelForLevel,
  intensityForLevel,
  onHintAvailable: (available) => { toolbar.setHintAvailable(available); syncToolbarButtons(); },
  onCompleteChanged: (complete) => modals.setActive(complete ? 'result' : 'none'),
});

// gameEvents 登録列: state / time-tick は header、set-level は header → DEV panel → PuzzleScene、
// level-clear は header → composition root、game-over は composition root、
// restart / continue / hint-granted / time-pause / time-resume は PuzzleScene。
onGameEvent(gameEventNames.levelClear, (detail) => {
  progressSync.attemptCleared(detail.levelNumber, detail.elapsedMs);
  levelFlow.markResultPending();
  syncToolbarButtons();
  const progress = recordClear(detail.levelNumber, !detail.reachedGameOver);
  resultView.render(buildResultViewModel(detail, progress));
  void statsFlow.levelCleared(detail.levelNumber, detail.elapsedMs);
});

onGameEvent(gameEventNames.attemptMetrics, (detail) => {
  progressSync.updateMetrics(detail);
});

onGameEvent(gameEventNames.gameOver, adFlow.handleGameOver);

if (import.meta.env.DEV) {
  devPanel = createDevPanel({
    initialLevelNumber: levelFlow.currentLevel,
    minimumLevelNumber,
    maximumLevelNumber,
    clampLevelNumber,
    getMaskId: (levelNumber) => getEntry(levelNumber).maskId,
    loadLevel: (levelNumber) => levelFlow.loadLevel(levelNumber),
    isBlocked: () => isModalOrAdFlowActive(true),
  });
}

/*
 * Phaser の ScaleManager は devicePixelRatio を見ない。RESIZE モードでは
 * canvas の実ピクセル数が CSS ピクセル数と等しくなるため、DPR 3 の実機では
 * 盤面が 1/3 解像度で描かれてブラウザに引き伸ばされ、矢印の縁がぎざついていた。
 *
 * そこで NONE モードにして「ゲーム単位 = 物理ピクセル」で描き、zoom = 1/dpr で
 * CSS 上の見た目の大きさを元に戻す。ゲーム単位が物理ピクセルになるので、
 * 画面基準の距離（ドラッグ判定・タップピング）は renderScale を掛けて扱う。
 */
const renderScale = Math.min(Math.max(window.devicePixelRatio || 1, 1), 3);
const gameStage = document.getElementById('game-stage');

function stageSize(): { width: number; height: number } {
  return {
    width: Math.max(1, Math.round((gameStage?.clientWidth ?? 1) * renderScale)),
    height: Math.max(1, Math.round((gameStage?.clientHeight ?? 1) * renderScale)),
  };
}

const initialSize = stageSize();
game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: 'game-stage',
  transparent: true,
  backgroundColor: 'rgba(0,0,0,0)',
  input: { activePointers: 3 },
  scale: {
    mode: Phaser.Scale.NONE,
    width: initialSize.width,
    height: initialSize.height,
    zoom: 1 / renderScale,
  },
  render: { antialias: true, roundPixels: false },
  scene: [new PuzzleScene(levelFlow.currentLevel)],
});

if (gameStage !== null) {
  const resizeObserver = new ResizeObserver(() => {
    const size = stageSize();
    if (game !== null && (game.scale.width !== size.width || game.scale.height !== size.height)) {
      game.scale.resize(size.width, size.height);
    }
  });
  resizeObserver.observe(gameStage);
}
